import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Gamepad2, Grid3x3, Brain } from "lucide-react";
import { Button } from "../components/ui/button";
import MemoryGame from "../components/MemoryGame";
import DreamQuiz from "../components/DreamQuiz";
import LanguageSelector from "../components/LanguageSelector";

export default function GamesPage({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeGame, setActiveGame] = useState(null);

  // Parse challenge data from URL
  const challengeData = useMemo(() => {
    if (searchParams.get("challenge") === "true") {
      return {
        score: searchParams.get("score") || "0",
        name: searchParams.get("name") || "?",
        type: searchParams.get("type") || "",
      };
    }
    return null;
  }, [searchParams]);

  // Auto-open quiz if challenge link
  useEffect(() => {
    if (challengeData) {
      setActiveGame("quiz");
    }
  }, [challengeData]);

  const handleChallengeComplete = () => {
    // Clear challenge params from URL after completing
    setSearchParams({});
  };

  const handleBackToGames = () => {
    setActiveGame(null);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50" data-testid="games-page">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="text-void/60 hover:text-void"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("nav.dashboard") || "Dashboard"}
              </Button>
              <div className="flex items-center gap-2">
                <Gamepad2 className="w-5 h-5 text-purple-500" />
                <h1 className="text-xl font-heading font-light text-void">
                  {t("games.title") || "Unipelit"}
                </h1>
              </div>
            </div>
            <div className="hidden sm:block">
              <LanguageSelector variant="minimal" />
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {!activeGame ? (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-heading font-light text-void mb-3">
                {t("games.title") || "Unipelit"}
              </h2>
              <p className="text-void/60 font-body text-sm max-w-lg mx-auto">
                {t("games.subtitle") || "Testaa tietämyksesi unista ja treenaa muistiasi unenomaisilla peleillä."}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {/* Memory Game Card */}
              <button
                data-testid="select-memory-game"
                onClick={() => setActiveGame("memory")}
                className="group text-left p-8 rounded-2xl border border-purple-200/50 bg-white/70 backdrop-blur-sm hover:bg-purple-50/80 hover:border-purple-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Grid3x3 className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-heading text-void mb-2">
                  {t("games.memoryTitle") || "Unisymbolien muistipeli"}
                </h3>
                <p className="text-sm text-void/60 font-body leading-relaxed">
                  {t("games.memoryShort") || "Etsi parit ja testaa muistiasi unenomaisilla symboleilla. 3 vaikeustasoa!"}
                </p>
                <div className="mt-4 flex gap-1">
                  {["\u{1F319}", "\u{2B50}", "\u{1F98B}", "\u{1F511}"].map((e, i) => (
                    <span key={i} className="text-xl opacity-60 group-hover:opacity-100 transition-opacity" style={{ transitionDelay: `${i * 50}ms` }}>{e}</span>
                  ))}
                </div>
              </button>

              {/* Quiz Card */}
              <button
                data-testid="select-quiz-game"
                onClick={() => setActiveGame("quiz")}
                className="group text-left p-8 rounded-2xl border border-purple-200/50 bg-white/70 backdrop-blur-sm hover:bg-purple-50/80 hover:border-purple-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Brain className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-heading text-void mb-2">
                  {t("games.quizTitle") || "Unitietovisailu"}
                </h3>
                <p className="text-sm text-void/60 font-body leading-relaxed">
                  {t("games.quizShort") || "Kuinka paljon tiedät unista? 20 kysymystä, 10 per kierros!"}
                </p>
                <div className="mt-4 flex gap-1">
                  {["\u{1F9E0}", "\u{1F4A1}", "\u{1F3AF}", "\u{1F31F}"].map((e, i) => (
                    <span key={i} className="text-xl opacity-60 group-hover:opacity-100 transition-opacity" style={{ transitionDelay: `${i * 50}ms` }}>{e}</span>
                  ))}
                </div>
              </button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              variant="ghost"
              onClick={handleBackToGames}
              className="mb-6 text-void/50 hover:text-void"
              data-testid="back-to-games"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("games.allGames") || "Kaikki pelit"}
            </Button>

            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-purple-100/50 p-6 sm:p-8">
              {activeGame === "memory" ? (
                <MemoryGame />
              ) : (
                <DreamQuiz
                  challengeData={challengeData}
                  onChallengeComplete={handleChallengeComplete}
                />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
